import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Tabs,
  InputNumber,
  Descriptions,
  Typography,
  Divider,
  Select,
  DatePicker,
  TimePicker,
  Checkbox
} from 'antd';
import {
  SearchOutlined,
  MailOutlined,
  GiftOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { compactParams, configAPI, mailAPI } from '../services/api';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

const SEND_MODE_OPTIONS = [
  { value: 'personal', label: '个人发送', icon: '👤', desc: '发送给指定用户' },
  { value: 'all_server', label: '全服发送', icon: '🌍', desc: '发送给全部玩家' },
  { value: 'online', label: '在线发送', icon: '🟢', desc: '发送给最近在线玩家' },
  { value: 'scheduled', label: '定时发送', icon: '⏰', desc: '在指定时间自动发送' },
  { value: 'interval', label: '周期发送', icon: '🔄', desc: '按间隔自动发送' }
];

const WEEKDAY_OPTIONS = [
  { value: 1, label: '星期一' },
  { value: 2, label: '星期二' },
  { value: 3, label: '星期三' },
  { value: 4, label: '星期四' },
  { value: 5, label: '星期五' },
  { value: 6, label: '星期六' },
  { value: 0, label: '星期日' }
];

const INTERVAL_UNITS = [
  { value: 'minutes', label: '分钟' },
  { value: 'hours', label: '小时' },
  { value: 'days', label: '天' }
];

const MAX_REWARD_COUNT = 2147483647;

const buildMailItemsPayload = (items = []) => (
  items.map((item) => ({
    type: item.type,
    id: item.id,
    count: item.count,
    level: item.level,
    name: item.name
  }))
);

const toIntervalMinutes = (value, unit = 'minutes') => {
  const safeValue = Math.max(1, Number(value) || 0);

  if (unit === 'hours') {
    return safeValue * 60;
  }

  if (unit === 'days') {
    return safeValue * 60 * 24;
  }

  return safeValue;
};

function MailManagement() {
  const [loading, setLoading] = useState(false);
  const [mails, setMails] = useState([]);
  const [logs, setLogs] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [logPagination, setLogPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [taskPagination, setTaskPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ keyword: '' });
  const [sendVisible, setSendVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentMail, setCurrentMail] = useState(null);
  const [sendForm] = Form.useForm();
  const [sendMode, setSendMode] = useState('personal');
  const [intervalUnit, setIntervalUnit] = useState('minutes');
  const [scheduledType, setScheduledType] = useState('once');
  const [itemList, setItemList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemSearchText, setItemSearchText] = useState('');
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [currentItemForLevel, setCurrentItemForLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const { current: pageCurrent, pageSize } = pagination;
  const selectedItemsRef = useRef([]);
  
  const [frameModalVisible, setFrameModalVisible] = useState(false);
  const [frameList, setFrameList] = useState([]);
  const [frameForm] = Form.useForm();
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [frameColorId, setFrameColorId] = useState(null);
  const [frameLevel, setFrameLevel] = useState(null);

  const [aozModalVisible, setAozModalVisible] = useState(false);
  const [aozForm] = Form.useForm();

  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  useEffect(() => {
    loadItemList();
    loadFrameList();
  }, []);

  const loadFrameList = async () => {
    try {
      const response = await configAPI.getFrames();
      if (response.data.success) {
        setFrameList(response.data.data);
      }
    } catch (error) {
      console.error('Load frame list error:', error);
    }
  };

  const loadScheduledTasks = async () => {
    try {
      const params = {
        page: taskPagination.current,
        limit: taskPagination.pageSize
      };

      const response = await mailAPI.getScheduledTasks(params);
      setScheduledTasks(response.data.data.list);
      setTaskPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total
      }));
    } catch (error) {
      console.error('Load scheduled tasks error:', error);
    }
  };

  const loadItemList = async () => {
    try {
      const response = await configAPI.getItems();
      if (response.data.success) {
        const items = response.data.data || [];
        const formattedItems = items.map(item => ({
          id: item.item_id || item.id,
          name: item.name || `道具${item.item_id || item.id}`,
          type: item.type || 'resource',
          levels: item.levels || [],
          hasMultipleLevels: item.hasMultipleLevels || false,
          defaultLevel: item.defaultLevel || null
        }));
        setItemList(formattedItems);
      }
    } catch (error) {
      console.error('Load item list error:', error);
    }
  };

  const loadMails = useCallback(async () => {
    setLoading(true);
    try {
      const params = compactParams({
        page: pageCurrent,
        limit: pageSize,
        ...filters
      });

      const response = await mailAPI.getList(params);
      setMails(response.data.data.list);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total
      }));
    } catch (error) {
      console.error('Load mails error:', error);
      message.error('加载邮件列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters, pageCurrent, pageSize]);

  useEffect(() => {
    loadMails();
  }, [loadMails]);

  const loadLogs = async () => {
    try {
      const params = {
        page: logPagination.current,
        limit: logPagination.pageSize
      };

      const response = await mailAPI.getLogs(params);
      setLogs(response.data.data.list);
      setLogPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total
      }));
    } catch (error) {
      console.error('Load logs error:', error);
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, keyword: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetail = (record) => {
    setCurrentMail(record);
    setDetailVisible(true);
  };

  const handleSendMail = () => {
    setSendVisible(true);
    sendForm.resetFields();
    setSendMode('personal');
    setScheduledType('once');
    setIntervalUnit('minutes');
    setSelectedItems([]);
    sendForm.setFieldsValue({ send_mode: 'personal', send_protect: false });
  };

  const handleAddItem = (item) => {
    if (item.levels && item.levels.length > 0) {
      const defaultLevel = item.levels[item.levels.length - 1]?.level ?? item.levels[0].level;
      setCurrentItemForLevel(item);
      setSelectedLevel(defaultLevel);
      setLevelModalVisible(true);
      return;
    }

    const level = item.defaultLevel || 1;
    const uniqueId = `${item.id}_${level}`;
    const exists = selectedItems.find(i => i.uniqueId === uniqueId);
    if (!exists) {
      setSelectedItems([...selectedItems, { ...item, level, count: 1, uniqueId }]);
    } else {
      message.warning('该道具已添加');
    }
  };

  const handleConfirmLevel = () => {
    if (!currentItemForLevel || !selectedLevel) return;

    const uniqueId = `${currentItemForLevel.id}_${selectedLevel}`;
    const exists = selectedItems.find(i => i.uniqueId === uniqueId);
    if (!exists) {
      setSelectedItems([...selectedItems, { ...currentItemForLevel, level: selectedLevel, count: 1, uniqueId }]);
    } else {
      message.warning('该道具已添加');
    }
    setLevelModalVisible(false);
    setCurrentItemForLevel(null);
    setSelectedLevel(null);
  };

  const handleRemoveItem = (uniqueId) => {
    setSelectedItems(prevItems => prevItems.filter(i => (i.uniqueId || i.id) !== uniqueId));
  };

  const handleItemLevelChange = (uniqueId, level) => {
    setSelectedItems(prevItems => {
      const newItems = prevItems.map(i => 
        (i.uniqueId || i.id) === uniqueId ? { ...i, level: parseInt(level) || 0 } : i
      );
      return newItems;
    });
  };

  const handleItemCountChange = (uniqueId, count) => {
    setSelectedItems(prevItems => {
      const newItems = prevItems.map(i => 
        (i.uniqueId || i.id) === uniqueId ? { ...i, count: parseInt(count) || 1 } : i
      );
      return newItems;
    });
  };

  const handleConfirmItems = () => {
    const itemsData = JSON.stringify(buildMailItemsPayload(selectedItemsRef.current));
    sendForm.setFieldsValue({
      items: itemsData
    });
    setItemModalVisible(false);
    message.success(`已添加 ${selectedItemsRef.current.length} 个道具`);
  };

  const handleSendSubmit = async (values) => {
    try {
      const items = buildMailItemsPayload(selectedItemsRef.current);

      const payload = {
        send_mode: sendMode,
        target_zone: values.target_zone || 'all',
        title: values.title,
        content: values.content,
        items: items,
        send_protect: !!values.send_protect,
        protect_minutes: values.send_protect ? values.protect_minutes : 0
      };

      if (sendMode === 'personal') {
        payload.user_id = values.user_id;
      }

      if (sendMode === 'scheduled') {
        if (!values.scheduled_time) {
          message.error('请选择定时发送时间');
          return;
        }
        payload.scheduled_time = values.scheduled_time.format('YYYY-MM-DD HH:mm:ss');
        payload.scheduled_type = scheduledType;
        payload.send_target = values.send_target || 'all_server';
        if (payload.send_target === 'online_duration') {
          payload.online_minutes = values.online_minutes;
        }
        if (scheduledType === 'weekly') {
          if (!values.weekday) {
            message.error('请选择星期几');
            return;
          }
          payload.weekday = values.weekday;
        }
      }

      if (sendMode === 'interval') {
        if (!values.interval_value || values.interval_value < 1) {
          message.error('请输入有效的间隔数值');
          return;
        }
        if (!values.end_time) {
          message.error('请选择结束时间');
          return;
        }
        
        payload.interval_minutes = toIntervalMinutes(values.interval_value, intervalUnit);
        payload.end_time = values.end_time.format('YYYY-MM-DD HH:mm:ss');
        if (values.repeat_count) {
          payload.repeat_count = values.repeat_count;
        }
      }

      const response = await mailAPI.send(payload);
      const responseMessage = response?.data?.message;
      const isAsyncTask = Boolean(response?.data?.data?.asyncTask);

      message.success(
        responseMessage || (
          sendMode === 'scheduled' ? '定时任务创建成功' :
          sendMode === 'interval' ? '周期任务创建成功' :
          '邮件发送成功'
        )
      );
      setSendVisible(false);
      sendForm.resetFields();
      setSelectedItems([]);
      loadMails();
      if (sendMode === 'scheduled' || sendMode === 'interval' || isAsyncTask) {
        loadScheduledTasks();
      }
    } catch (error) {
      console.error('Send mail error:', error);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这封邮件吗？',
      onOk: async () => {
        try {
          await mailAPI.delete(id);
          message.success('删除成功');
          loadMails();
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleDeleteScheduledTask = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个定时任务吗？',
      onOk: async () => {
        try {
          await mailAPI.deleteScheduledTask(id);
          message.success('删除成功');
          loadScheduledTasks();
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleSendFrame = async (values) => {
    try {
      if (!selectedFrame) {
        message.error('请选择装扮');
        return;
      }

      if (!frameColorId) {
        message.error('请选择装扮颜色');
        return;
      }

      if (!frameLevel) {
        message.error('请选择装扮等级');
        return;
      }

      await mailAPI.sendFrame({
        user_id: values.user_id,
        frame_id: selectedFrame,
        color_id: frameColorId,
        level: frameLevel,
        title: values.title,
        content: values.content
      });

      message.success('装扮发送成功');
      setFrameModalVisible(false);
      frameForm.resetFields();
      setSelectedFrame(null);
      setFrameColorId(null);
      setFrameLevel(null);
    } catch (error) {
      message.error('发送装扮失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleSendAoz = () => {
    setAozModalVisible(true);
    aozForm.resetFields();
    aozForm.setFieldsValue({ send_mode: 'personal' });
  };

  const handleSendAozSubmit = async (values) => {
    try {
      const payload = {
        send_mode: values.send_mode,
        aozCoin: values.aozCoin || 0,
        freeAozCoin: values.freeAozCoin || 0,
        gmAozCoin: values.gmAozCoin || 0
      };

      if (values.send_mode === 'personal') {
        payload.user_id = values.user_id;
      }

      if (values.send_mode === 'scheduled') {
        if (!values.scheduled_time) {
          message.error('请选择定时发送时间');
          return;
        }
        payload.scheduled_time = values.scheduled_time.format('YYYY-MM-DD HH:mm:ss');
        payload.scheduled_type = values.scheduled_type || 'once';
        if (values.scheduled_type === 'weekly') {
          if (!values.weekday) {
            message.error('请选择星期几');
            return;
          }
          payload.weekday = values.weekday;
        }
      }

      if (values.send_mode === 'interval') {
        if (!values.interval_value || values.interval_value < 1) {
          message.error('请输入有效的间隔数值');
          return;
        }
        if (!values.end_time) {
          message.error('请选择结束时间');
          return;
        }
        
        payload.interval_minutes = toIntervalMinutes(values.interval_value, values.interval_unit);
        payload.end_time = values.end_time.format('YYYY-MM-DD HH:mm:ss');
      }

      await mailAPI.sendAoz(payload);
      message.success('AOZ 发送成功');
      setAozModalVisible(false);
      aozForm.resetFields();
    } catch (error) {
      message.error('AOZ 发送失败：' + (error.response?.data?.message || error.message));
    }
  };

  const parseItems = (itemsStr) => {
    try {
      if (!itemsStr || itemsStr === 'null' || itemsStr === '') return [];
      
      let items;
      if (typeof itemsStr === 'string') {
        try {
          items = JSON.parse(itemsStr);
        } catch (e) {
          const parts = itemsStr.split(',').map(s => parseInt(s.trim()));
          if (parts.length > 0 && !isNaN(parts[0])) {
            items = parts;
          } else {
            return [];
          }
        }
      } else {
        items = itemsStr;
      }
      
      if (!Array.isArray(items) || items.length === 0) return [];
      
      if (typeof items[0] === 'object') {
        return items.map((item, idx) => ({
          key: idx,
          id: item.gid || item.id || 'N/A',
          name: item.name || '',
          level: item.level !== undefined ? item.level : (item.type !== undefined ? item.type : 0),
          count: item.count || 'N/A'
        }));
      } else if (typeof items[0] === 'number' && items.length % 3 === 0) {
        const result = [];
        for (let i = 0; i < items.length; i += 3) {
          result.push({
            key: i,
            id: items[i],
            level: items[i + 1],
            count: items[i + 2]
          });
        }
        return result;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const getItemName = (item) => {
    if (item?.name) {
      return item.name;
    }

    const matchedItem = itemList.find((configItem) => String(configItem.id) === String(item?.id));
    return matchedItem?.name || '未知道具';
  };

  const currentSendModeOption = useMemo(
    () => SEND_MODE_OPTIONS.find((option) => option.value === sendMode) || SEND_MODE_OPTIONS[0],
    [sendMode]
  );

  const filteredItemList = useMemo(() => {
    const searchText = itemSearchText.toLowerCase().trim();
    if (!searchText) {
      return itemList;
    }

    return itemList.filter((item) => {
      const itemId = String(item.id);
      const itemName = (item.name || '').toLowerCase();
      const levelNames = (item.levels || []).map((level) => level.name || '').join(' ').toLowerCase();
      return itemId.includes(searchText) || itemName.includes(searchText) || levelNames.includes(searchText);
    });
  }, [itemList, itemSearchText]);

  const mailColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '用户 ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (text) => renderTableStack(<Text code>{text}</Text>, '收件目标')
    },
    {
      title: '邮件标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 200,
      render: (text, record) => renderTableStack(text || '未命名邮件', record.content || '无正文预览', record.create_time ? new Date(record.create_time).toLocaleString('zh-CN') : null)
    },
    {
      title: '邮件内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      width: 220,
      render: (text) => renderTableStack(text || '无邮件内容', text ? '支持在详情中查看完整正文' : '邮件正文为空')
    },
    {
      title: '状态',
      dataIndex: 'is_read',
      key: 'is_read',
      width: 80,
      render: (is_read) => (
        <Tag color={is_read ? 'green' : 'orange'}>
          {is_read ? '已读' : '未读'}
        </Tag>
      )
    },
    {
      title: '发送时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 150,
      render: (time) => renderTableStack(time ? new Date(time).toLocaleString('zh-CN') : '-', '创建时间')
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  const logColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '用户 ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: '道具列表',
      dataIndex: 'items',
      key: 'items',
      width: 240,
      render: (items) => {
        const parsedItems = parseItems(items);
        if (parsedItems.length === 0) return <Text type="secondary">无道具</Text>;
        return (
          <Space direction="vertical" size={6}>
            {parsedItems.map((item) => (
              <Tag key={item.key} color="blue">
                {getItemName(item)} x{item.count} / Lv.{item.level}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
      width: 90
    },
    {
      title: '发放时间',
      dataIndex: 'grant_time',
      key: 'grant_time',
      width: 150,
      render: (time) => {
        if (!time) return '-';
        return new Date(time).toLocaleString('zh-CN');
      }
    }
  ];

  const taskColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '发送模式',
      dataIndex: 'send_mode',
      key: 'send_mode',
      width: 110,
      render: (mode) => {
        const modeMap = {
          'personal': '个人发送',
          'all_server': '全服发送',
          'online': '在线发送',
          'scheduled': '定时发送',
          'interval': '周期发送'
        };
        return <Tag color={mode === 'interval' ? 'purple' : mode === 'scheduled' ? 'blue' : ''}>{modeMap[mode] || mode}</Tag>;
      }
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 160,
      ellipsis: true
    },
    {
      title: '保护',
      key: 'protect',
      width: 120,
      render: (_, record) => {
        const enabled = record.send_protect === 1 || record.send_protect === true || record.send_protect === '1';
        if (!enabled) {
          return <Tag>无</Tag>;
        }
        const minutes = Number(record.protect_minutes || 0);
        if (minutes > 0 && minutes % 1440 === 0) {
          return <Tag color="gold">{minutes / 1440}天保护</Tag>;
        }
        if (minutes > 0 && minutes % 60 === 0) {
          return <Tag color="gold">{minutes / 60}小时保护</Tag>;
        }
        return <Tag color="gold">{minutes || 0}分钟保护</Tag>;
      }
    },
    {
      title: '间隔/时间',
      key: 'interval_info',
      width: 110,
      render: (_, record) => {
        if (record.send_mode === 'interval') {
          const minutes = record.interval_minutes;
          if (minutes >= 60 * 24 && minutes % (60 * 24) === 0) {
            return `${minutes / (60 * 24)}天`;
          } else if (minutes >= 60 && minutes % 60 === 0) {
            return `${minutes / 60}小时`;
          } else {
            return `${minutes}分钟`;
          }
        } else if (record.scheduled_time) {
          return new Date(record.scheduled_time).toLocaleString('zh-CN', { 
            month: '2-digit', 
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        return '-';
      }
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 150,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    },
    {
      title: '执行次数',
      key: 'executed',
      width: 100,
      render: (_, record) => {
        if (record.send_mode === 'interval') {
          return `${record.executed_count || 0}${record.repeat_count ? '/' + record.repeat_count : ''}`;
        }
        return '-';
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'pending': '等待中',
          'running': '运行中',
          'sent': '已发送',
          'failed': '失败',
          'stopped': '已停止'
        };
        const colorMap = {
          'pending': 'orange',
          'running': 'blue',
          'sent': 'green',
          'failed': 'red',
          'stopped': 'gray'
        };
        return <Tag color={colorMap[status]}>{statusMap[status] || status}</Tag>;
      }
    },
    {
      title: '已发送/总数',
      key: 'count',
      width: 100,
      render: (_, record) => `${record.sent_count || 0}/${record.total_count || 0}`
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteScheduledTask(record.id)}
        >
          删除
        </Button>
      )
    }
  ];

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">邮件管理</div>
          <div className="page-subtitle">把邮件发送、道具发放日志和定时任务集中在一页，尽量减少跳转和重复输入。</div>
        </div>
        <div className="page-header-actions">
          <Tag color="blue">邮件 {mails.length} 条</Tag>
          <Tag color="purple">定时任务 {scheduledTasks.length} 个</Tag>
        </div>
      </div>

      <div className="page-note">
        <div className="page-note-text">
          <span className="page-note-strong">邮件、日志和定时任务集中在同一页处理。</span>
          发送入口统一放在右上角，搜索和刷新都只影响当前 Tab。
        </div>
        <Text type="secondary">当前邮件总数 {pagination.total || 0}</Text>
      </div>

      <Card 
        className="table-card"
        title="邮件中心"
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<MailOutlined />}
              onClick={handleSendMail}
            >
              发送邮件
            </Button>
            <Button 
              type="primary" 
              icon={<GiftOutlined />}
              onClick={() => {
                setFrameModalVisible(true);
                frameForm.resetFields();
                setSelectedFrame(null);
                setFrameColorId(null);
                setFrameLevel(null);
              }}
            >
              发送装扮
            </Button>
            <Button 
              type="primary" 
              icon={<DollarOutlined />}
              onClick={handleSendAoz}
            >
              发送 AOZ
            </Button>
          </Space>
        }
      >
        <Tabs 
          defaultActiveKey="mails"
          onChange={(key) => {
            if (key === 'logs') {
              loadLogs();
            } else if (key === 'scheduled') {
              loadScheduledTasks();
            }
          }}
        >
          <Tabs.TabPane tab="邮件列表" key="mails">
            <div className="detail-section">
              <div className="section-note">
                <div className="section-note-text">
                  <span className="section-note-strong">邮件列表默认按发送时间查看。</span>
                  搜索标题或正文后会回到第一页，方便连续核对发送结果。
                </div>
                <Tag color="blue">当前页 {mails.length} 条</Tag>
              </div>
              <div className="section-toolbar">
                <div className="section-toolbar-main">
                  <Input
                    placeholder="搜索邮件标题或内容"
                    style={{ width: 300 }}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    suffix={<SearchOutlined />}
                  />
                </div>
                <div className="section-toolbar-actions">
                  <Button icon={<ReloadOutlined />} onClick={loadMails}>
                    刷新
                  </Button>
                </div>
              </div>

              <Table
                columns={mailColumns}
                dataSource={mails}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: 900 }}
                locale={{ emptyText: renderEmptyPanel('暂无邮件数据', '当前条件下没有可展示的邮件记录。') }}
                pagination={{
                  ...pagination,
                  onChange: (page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                  }
                }}
              />
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab="道具发放日志" key="logs">
            <Table
              columns={logColumns}
              dataSource={logs}
              rowKey="id"
              size="small"
              scroll={{ x: 760 }}
              locale={{ emptyText: renderEmptyPanel('暂无发放日志', '发送带道具邮件后，这里会记录发放流水。') }}
              pagination={{
                ...logPagination,
                onChange: (page, pageSize) => {
                  setLogPagination(prev => ({ ...prev, current: page, pageSize }));
                  setTimeout(() => loadLogs(), 0);
                }
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="定时任务" key="scheduled">
            <Table
              columns={taskColumns}
              dataSource={scheduledTasks}
              rowKey="id"
              size="small"
              scroll={{ x: 900 }}
              locale={{ emptyText: renderEmptyPanel('暂无定时任务', '创建定时或周期任务后，这里会展示执行状态。') }}
              pagination={{
                ...taskPagination,
                onChange: (page, pageSize) => {
                  setTaskPagination(prev => ({ ...prev, current: page, pageSize }));
                  setTimeout(() => loadScheduledTasks(), 0);
                }
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* 发送邮件弹窗 */}
      <Modal
        title={null}
        open={sendVisible}
        onCancel={() => setSendVisible(false)}
        footer={null}
        width={1080}
        className="mail-send-modal"
      >
        <Form form={sendForm} layout="vertical" onFinish={handleSendSubmit}>
          <Form.Item name="send_mode" initialValue="personal" hidden>
            <Input />
          </Form.Item>

          <div className="mail-modal-shell">
            <div className="mail-modal-hero">
              <div>
                <div className="mail-modal-title">发送邮件</div>
                <div className="mail-modal-subtitle">把目标、时间、正文和附件收进同一张发送工作台，提交前直接完成最终确认。</div>
              </div>
              <div className="mail-modal-hero-tags">
                <Tag color="blue">{currentSendModeOption.label}</Tag>
                <Tag color="green">已选道具 {selectedItems.length} 项</Tag>
              </div>
            </div>

            <div className="mail-mode-grid">
              {SEND_MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`mail-mode-card${sendMode === option.value ? ' mail-mode-card-active' : ''}`}
                  onClick={() => {
                    setSendMode(option.value);
                    sendForm.setFieldValue('send_mode', option.value);
                  }}
                >
                  <span className="mail-mode-card-icon">{option.icon}</span>
                  <span className="mail-mode-card-body">
                    <strong>{option.label}</strong>
                    <span>{option.desc}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="mail-send-workspace">
              <div className="mail-send-main">
                <div className="mail-form-section">
                  <div className="mail-form-section-head">
                    <div className="mail-form-section-title">发送目标与时间</div>
                    <div className="mail-form-section-subtitle">根据当前发送模式填写目标用户、定时或周期参数。</div>
                  </div>

                  {sendMode === 'personal' && (
                    <Form.Item
                      name="user_id"
                      label="用户 ID"
                      rules={[{ required: true, message: '请输入用户 ID' }]}
                    >
                      <InputNumber min={1} className="field-width-full" placeholder="请输入用户 ID" />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="target_zone"
                    label="目标区服"
                    initialValue="all"
                    rules={[{ required: true, message: '请选择目标区服' }]}
                  >
                    <Select size="large">
                      <Option value="all">全区（包含以后新增区服）</Option>
                      <Option value="1">一区</Option>
                      <Option value="2">二区</Option>
                      <Option value="3">三区</Option>
                    </Select>
                  </Form.Item>

                  {(sendMode === 'all_server' || sendMode === 'online') && (
                    <div className="mail-inline-notice">
                      <div className="mail-inline-notice-title">{sendMode === 'all_server' ? '全服发送' : '在线发送'}</div>
                      <div className="mail-inline-notice-text">
                        {sendMode === 'all_server'
                          ? '提交后会对当前模式覆盖的所有玩家创建邮件，请再次确认标题、正文与附件。'
                          : '提交后只会对当前在线玩家发送邮件，适合临时补偿或活动发放。'}
                      </div>
                    </div>
                  )}

                  {sendMode === 'scheduled' && (
                    <div className="mail-form-grid">
                      <Form.Item name="send_target" label="奖励对象" initialValue="all_server">
                        <Select size="large">
                          <Option value="all_server">全服玩家</Option>
                          <Option value="online">当前在线玩家</Option>
                          <Option value="online_duration">今日在线达到指定时长</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, current) => prev.send_target !== current.send_target}>
                        {({ getFieldValue }) => getFieldValue('send_target') === 'online_duration' ? (
                          <Form.Item
                            name="online_minutes"
                            label="今日在线时长"
                            rules={[{ required: true, message: '请输入在线时长' }]}
                          >
                            <InputNumber min={1} max={1440} precision={0} addonAfter="分钟" className="field-width-full" />
                          </Form.Item>
                        ) : null}
                      </Form.Item>
                      <Form.Item
                        name="scheduled_type"
                        label="定时类型"
                        initialValue="once"
                      >
                        <Select value={scheduledType} onChange={setScheduledType} size="large">
                          <Option value="once">单次定时</Option>
                          <Option value="daily">每天发送</Option>
                          <Option value="weekly">每周发送</Option>
                        </Select>
                      </Form.Item>

                      {scheduledType === 'once' && (
                        <Form.Item
                          name="scheduled_time"
                          label="发送时间"
                          rules={[{ required: scheduledType === 'once', message: '请选择定时发送时间' }]}
                        >
                          <DatePicker
                            showTime
                            className="field-width-full"
                            placeholder="请选择定时发送时间"
                            disabledDate={(current) => current && current < new Date()}
                          />
                        </Form.Item>
                      )}

                      {scheduledType === 'daily' && (
                        <>
                          <Form.Item
                            name="scheduled_time"
                            label="每天发送时间"
                            rules={[{ required: scheduledType === 'daily', message: '请选择每天发送时间' }]}
                          >
                            <TimePicker
                              className="field-width-full"
                              placeholder="请选择每天发送时间"
                              format="HH:mm"
                            />
                          </Form.Item>
                          <Form.Item
                            name="end_time"
                            label="结束日期"
                            rules={[{ required: scheduledType === 'daily', message: '请选择结束日期' }]}
                          >
                            <DatePicker
                              className="field-width-full"
                              placeholder="请选择结束日期"
                              disabledDate={(current) => current && current < new Date()}
                            />
                          </Form.Item>
                        </>
                      )}

                      {scheduledType === 'weekly' && (
                        <>
                          <Form.Item
                            name="weekday"
                            label="星期几"
                            rules={[{ required: scheduledType === 'weekly', message: '请选择星期几' }]}
                          >
                            <Select placeholder="请选择星期几" size="large">
                              {WEEKDAY_OPTIONS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            name="scheduled_time"
                            label="每周发送时间"
                            rules={[{ required: scheduledType === 'weekly', message: '请选择每周发送时间' }]}
                          >
                            <TimePicker
                              className="field-width-full"
                              placeholder="请选择每周发送时间"
                              format="HH:mm"
                            />
                          </Form.Item>
                          <Form.Item
                            name="end_time"
                            label="结束日期"
                            rules={[{ required: scheduledType === 'weekly', message: '请选择结束日期' }]}
                          >
                            <DatePicker
                              className="field-width-full"
                              placeholder="请选择结束日期"
                              disabledDate={(current) => current && current < new Date()}
                            />
                          </Form.Item>
                        </>
                      )}
                    </div>
                  )}

                  {sendMode === 'interval' && (
                    <div className="mail-form-grid">
                      <Form.Item
                        name="interval_value"
                        label="间隔时间"
                        rules={[{ required: sendMode === 'interval', message: '请输入间隔数值' }]}
                      >
                        <InputNumber
                          min={1}
                          max={365}
                          className="field-width-calc-120"
                          placeholder="请输入间隔数值"
                          addonBefore={(
                            <Select
                              value={intervalUnit}
                              onChange={setIntervalUnit}
                              className="addon-select-100"
                            >
                              {INTERVAL_UNITS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                              ))}
                            </Select>
                          )}
                        />
                      </Form.Item>
                      <Form.Item
                        name="end_time"
                        label="结束时间"
                        rules={[{ required: sendMode === 'interval', message: '请选择结束时间' }]}
                      >
                        <DatePicker
                          showTime
                          className="field-width-full"
                          placeholder="请选择结束时间"
                          disabledDate={(current) => current && current < new Date()}
                        />
                      </Form.Item>
                      <Form.Item
                        name="repeat_count"
                        label="重复次数（可选）"
                      >
                        <InputNumber
                          min={1}
                          className="field-width-full"
                          placeholder="留空则持续执行到结束时间"
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>

                <div className="mail-form-section">
                  <div className="mail-form-section-head">
                    <div className="mail-form-section-title">邮件内容</div>
                    <div className="mail-form-section-subtitle">填写最终发送给玩家的标题和正文。</div>
                  </div>
                  <Form.Item
                    name="title"
                    label="邮件标题"
                    rules={[{ required: true, message: '请输入邮件标题' }]}
                  >
                    <Input placeholder="请输入邮件标题" />
                  </Form.Item>
                  <Form.Item
                    name="content"
                    label="邮件内容"
                    rules={[{ required: true, message: '请输入邮件内容' }]}
                  >
                    <TextArea rows={8} placeholder="请输入邮件内容" />
                  </Form.Item>
                </div>
              </div>

              <div className="mail-send-side">
                <div className="mail-form-section">
                  <div className="mail-form-section-head">
                    <div className="mail-form-section-title">附件与道具</div>
                    <div className="mail-form-section-subtitle">先从物品库选择，再在这里调整等级、数量或移除。</div>
                  </div>
                  <div className="mail-side-actions">
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setItemModalVisible(true)}
                      type="primary"
                    >
                      打开物品库
                    </Button>
                    <Tag color="green">已选 {selectedItems.length} 项</Tag>
                  </div>

                  {selectedItems.length > 0 ? (
                    <div className="mail-selected-list">
                      {selectedItems.map((item) => (
                        <div key={item.uniqueId || item.id} className="mail-selected-item">
                          <div className="mail-selected-item-main">
                            <div className="mail-selected-item-title">{item.name || `道具${item.id}`}{item.level ? ` Lv.${item.level}` : ''}</div>
                            <div className="mail-selected-item-meta">道具 ID {item.id}</div>
                          </div>
                          <div className="mail-selected-item-controls">
                            <InputNumber
                              min={0}
                              max={99999}
                              value={item.level || 0}
                              onChange={(value) => handleItemLevelChange(item.uniqueId || item.id, value)}
                              addonBefore="等级"
                              size="small"
                              className="field-width-120"
                            />
                            <InputNumber
                              min={1}
                              max={MAX_REWARD_COUNT}
                              value={item.count}
                              onChange={(value) => handleItemCountChange(item.uniqueId || item.id, value)}
                              addonBefore="数量"
                              size="small"
                              className="field-width-120"
                            />
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<MinusCircleOutlined />}
                              onClick={() => handleRemoveItem(item.uniqueId || item.id)}
                            >
                              移除
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mail-empty-panel">
                      <div className="mail-empty-panel-title">暂未添加附件</div>
                      <div className="mail-empty-panel-text">点击上方“打开物品库”后，从配置列表里选择要附带的道具。</div>
                    </div>
                  )}

                  <Form.Item name="items" hidden>
                    <Input />
                  </Form.Item>
                </div>

                <div className="mail-form-section">
                  <div className="mail-form-section-head">
                    <div className="mail-form-section-title">发送保护</div>
                    <div className="mail-form-section-subtitle">开启后不发放和平守护道具，直接给目标玩家附加保护效果。</div>
                  </div>
                  <Form.Item name="send_protect" valuePropName="checked" initialValue={false} className="mail-checkbox-item">
                    <Checkbox>启用保护效果</Checkbox>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.send_protect !== currentValues.send_protect}
                  >
                    {({ getFieldValue }) => (
                      getFieldValue('send_protect') ? (
                        <Form.Item
                          name="protect_minutes"
                          label="保护时长"
                          initialValue={480}
                          rules={[{ required: true, message: '请选择保护时长' }]}
                        >
                          <Select placeholder="请选择保护时长" size="large">
                            <Option value={120}>2小时</Option>
                            <Option value={480}>8小时</Option>
                            <Option value={1440}>1天</Option>
                            <Option value={4320}>3天</Option>
                          </Select>
                        </Form.Item>
                      ) : null
                    )}
                  </Form.Item>
                </div>
              </div>
            </div>

            <div className="mail-modal-submit">
              <Button type="primary" htmlType="submit" size="large">
                {sendMode === 'scheduled' ? '创建定时任务' :
                 sendMode === 'interval' ? '创建周期任务' :
                 '发送邮件'}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* 道具选择弹窗 */}
      <Modal
        title={null}
        open={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        onOk={handleConfirmItems}
        width={1120}
        className="mail-item-modal"
        footer={[
          <Button key="cancel" onClick={() => setItemModalVisible(false)}>
            取消
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmItems}>
            确认选择 ({selectedItems.length}个道具)
          </Button>
        ]}
      >
        <div className="mail-item-picker">
          <div className="mail-modal-hero mail-modal-hero-compact">
            <div>
              <div className="mail-modal-title">物品选择</div>
              <div className="mail-modal-subtitle">左侧浏览配置道具库，右侧实时查看已选附件并继续调整等级与数量。</div>
            </div>
            <div className="mail-modal-hero-tags">
              <Tag color="blue">可选 {filteredItemList.length} 个</Tag>
              <Tag color="green">已选 {selectedItems.length} 个</Tag>
            </div>
          </div>

          <div className="mail-item-picker-toolbar">
            <Input
              placeholder="搜索道具：输入 ID 或名称"
              allowClear
              value={itemSearchText}
              onChange={(e) => setItemSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              className="mail-item-search"
            />
          </div>

          <div className="mail-item-picker-body">
            <div className="mail-item-library">
              {filteredItemList.length > 0 ? (
                <div className="mail-item-grid">
                  {filteredItemList.map((record) => {
                    const isAdded = record.levels && record.levels.length > 0
                      ? record.levels.some((lvl) => selectedItems.some((si) => si.uniqueId === `${record.id}_${lvl.level}`))
                      : selectedItems.some((si) => si.uniqueId === `${record.id}_${record.defaultLevel || 1}`);

                    const qualityColors = ['default', 'green', 'blue', 'purple', 'orange', 'red'];

                    return (
                      <div key={record.id} className={`mail-item-card${isAdded ? ' mail-item-card-added' : ''}`}>
                        <div className="mail-item-card-head">
                          <div>
                            <div className="mail-item-card-title">{record.name || '未知道具'}</div>
                            <div className="mail-item-card-subtitle">ID {record.id}</div>
                          </div>
                          <Tag color={qualityColors[record.quality] || 'default'}>{record.quality || 0}星</Tag>
                        </div>
                        <div className="mail-item-card-meta">
                          <span>{record.levels && record.levels.length > 0 ? `${record.levels.length} 个等级` : '单等级道具'}</span>
                          <span>{record.type || 'resource'}</span>
                        </div>
                        <div className="mail-item-card-desc">{record.description || '暂无描述'}</div>
                        <Button
                          type={isAdded ? 'default' : 'primary'}
                          disabled={isAdded}
                          onClick={() => handleAddItem(record)}
                          block
                        >
                          {isAdded ? '已添加' : '加入附件'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                renderEmptyPanel('没有匹配道具', '请尝试搜索其他道具 ID 或名称。')
              )}
            </div>

            <div className="mail-item-sidebar">
              <div className="mail-form-section mail-form-section-tight">
                <div className="mail-form-section-head">
                  <div className="mail-form-section-title">已选附件</div>
                  <div className="mail-form-section-subtitle">在确认前可继续修改等级、数量或移除单项。</div>
                </div>

                {selectedItems.length > 0 ? (
                  <div className="mail-selected-list">
                    {selectedItems.map((item) => (
                      <div key={item.uniqueId || item.id} className="mail-selected-item">
                        <div className="mail-selected-item-main">
                          <div className="mail-selected-item-title">{item.name || `道具${item.id}`}{item.level ? ` Lv.${item.level}` : ''}</div>
                          <div className="mail-selected-item-meta">道具 ID {item.id}</div>
                        </div>
                        <div className="mail-selected-item-controls">
                          <InputNumber
                            min={0}
                            max={99999}
                            value={item.level || 0}
                            onChange={(value) => handleItemLevelChange(item.uniqueId || item.id, value)}
                            addonBefore="等级"
                            size="small"
                            className="field-width-120"
                          />
                          <InputNumber
                            min={1}
                            max={MAX_REWARD_COUNT}
                            value={item.count}
                            onChange={(value) => handleItemCountChange(item.uniqueId || item.id, value)}
                            addonBefore="数量"
                            size="small"
                            className="field-width-120"
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<MinusCircleOutlined />}
                            onClick={() => handleRemoveItem(item.uniqueId || item.id)}
                          >
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mail-empty-panel">
                    <div className="mail-empty-panel-title">右侧清单为空</div>
                    <div className="mail-empty-panel-text">从左侧物品库加入道具后，这里会实时显示确认结果。</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={`选择等级 - ${currentItemForLevel?.name || ''}`}
        open={levelModalVisible}
        onCancel={() => {
          setLevelModalVisible(false);
          setCurrentItemForLevel(null);
          setSelectedLevel(null);
        }}
        onOk={handleConfirmLevel}
        width={440}
        okText="确认选择"
        cancelText="取消"
      >
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table
            columns={[
              { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (level, record) => <Tag color="blue">{record.name || `Lv.${level}`}</Tag> },
              { title: '品质', dataIndex: 'quality', key: 'quality', width: 70, render: (quality) => <Tag>{quality || '-'}</Tag> },
              { title: '描述', dataIndex: 'description', key: 'description', width: 150, ellipsis: true, render: (text) => <Text type="secondary" ellipsis={{ tooltip: text || '-' }}>{text || '-'}</Text> },
              {
                title: '操作', key: 'action', width: 88,
                render: (_, record) => (
                  <Button size="small" type={selectedLevel === record.level ? 'primary' : 'default'} onClick={() => setSelectedLevel(record.level)}>
                    {selectedLevel === record.level ? '已选' : '选择'}
                  </Button>
                )
              }
            ]}
            dataSource={currentItemForLevel?.levels || []}
            rowKey="level"
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 378 }}
          />
        </div>
      </Modal>

      {/* 邮件详情弹窗 */}
      <Modal
        title="邮件详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {currentMail && (
          <div className="detail-section">
            <div className="section-note">
              <div className="section-note-text">
                <span className="section-note-strong">{currentMail.title || '当前邮件'}</span>
                用于核对正文、状态和附件内容，避免重复发送或误删。
              </div>
              <Tag color={currentMail.is_read ? 'green' : 'orange'}>
                {currentMail.is_read ? '已读' : '未读'}
              </Tag>
            </div>
            <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{currentMail.id}</Descriptions.Item>
            <Descriptions.Item label="用户 ID">{currentMail.user_id}</Descriptions.Item>
            <Descriptions.Item label="标题">{currentMail.title}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={currentMail.is_read ? 'green' : 'orange'}>
                {currentMail.is_read ? '已读' : '未读'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="发送时间">
              {currentMail.send_time ? new Date(currentMail.send_time * 1000).toLocaleString('zh-CN') : '未发送'}
            </Descriptions.Item>
            <Descriptions.Item label="邮件类型">
              {currentMail.mail_type || '普通邮件'}
            </Descriptions.Item>
            <Descriptions.Item label="内容" span={2}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{currentMail.content}</div>
            </Descriptions.Item>
            {(currentMail.reward || currentMail.items) && (
              <Descriptions.Item label="道具" span={2}>
                <div className="selection-panel">
                  {(() => {
                    const rewardStr = currentMail.reward || currentMail.items;
                    const parsedItems = parseItems(rewardStr);
                    if (parsedItems.length === 0) {
                      return <Text type="secondary">无道具</Text>;
                    }
                    return (
                      <div className="selection-list">
                        {parsedItems.map((item) => (
                          <div className="selection-item" key={item.key}>
                            <div className="selection-item-main">
                              <div className="selection-item-title">{getItemName(item)}</div>
                              <div className="selection-item-meta">道具 ID {item.id} / 等级 {item.level}</div>
                            </div>
                            <div className="selection-item-controls">
                              <Tag color="blue">x{item.count}</Tag>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </Descriptions.Item>
            )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 发送装扮弹窗 */}
      <Modal
        title="发送装扮道具"
        open={frameModalVisible}
        onCancel={() => {
          setFrameModalVisible(false);
          frameForm.resetFields();
          setSelectedFrame(null);
          setFrameColorId(null);
          setFrameLevel(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={frameForm} layout="vertical" onFinish={handleSendFrame}>
          <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
            <div className="page-note-text">
              <span className="page-note-strong">装扮邮件会发送到指定用户。</span>
              先确认用户 ID，再选择颜色和等级，标题与正文可按活动文案调整。
            </div>
          </div>
          <Form.Item
            name="user_id"
            label="用户 ID"
            rules={[{ required: true, message: '请输入用户 ID' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入用户 ID" />
          </Form.Item>

          {selectedFrame && (
            <>
              <Form.Item label="装扮颜色" required>
                <Select
                  placeholder="请选择装扮颜色"
                  value={frameColorId}
                  onChange={(value) => setFrameColorId(value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {(() => {
                    const colors = frameList.filter(f => f.item_id === selectedFrame && f.category === 'frame_color');
                    return colors.map(color => (
                      <Option key={color.color_id} value={color.color_id}>
                        {color.name || `颜色 ${color.color_id}`}
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>

              <Form.Item label="装扮等级" required>
                <Select
                  placeholder="请选择装扮等级"
                  value={frameLevel}
                  onChange={(value) => setFrameLevel(value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {(() => {
                    const levels = frameList.filter(f => f.item_id === selectedFrame && f.category === 'frame_level');
                    return levels.map(lvl => (
                      <Option key={lvl.level} value={lvl.level}>
                        等级 {lvl.level} (战力：{lvl.fightPower})
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item
            name="title"
            label="邮件标题"
            initialValue="装扮奖励"
          >
            <Input placeholder="请输入邮件标题" />
          </Form.Item>

          <Form.Item
            name="content"
            label="邮件内容"
            initialValue="恭喜您获得精美装扮！"
          >
            <TextArea rows={4} placeholder="请输入邮件内容" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              发送装扮
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 发送 AOZ 弹窗 */}
      <Modal
        title="发送 AOZ"
        open={aozModalVisible}
        onCancel={() => {
          setAozModalVisible(false);
          aozForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={aozForm} layout="vertical" onFinish={handleSendAozSubmit}>
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.send_mode !== currentValues.send_mode}>
            {({ getFieldValue }) => {
              const mode = getFieldValue('send_mode') || 'personal';
              const modeMap = {
                personal: '发送给单个玩家，适合补发或人工处理。',
                all_server: '广播给全服玩家，提交前请再次确认数量。',
                online: '只发送给在线玩家，适合临时活动奖励。',
                scheduled: '创建定时任务，到点后再自动发放。',
                interval: '按固定间隔重复发放，适合周期补给。'
              };

              return (
                <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
                  <div className="page-note-text">
                    <span className="page-note-strong">AOZ 发放模式：{mode}</span>
                    {modeMap[mode]}
                  </div>
                </div>
              );
            }}
          </Form.Item>
          <Form.Item
            name="send_mode"
            label="发送模式"
            rules={[{ required: true, message: '请选择发送模式' }]}
          >
            <Select placeholder="请选择发送模式">
              <Option value="personal">个人发送</Option>
              <Option value="all_server">全服发送</Option>
              <Option value="online">在线发送</Option>
              <Option value="scheduled">定时发送</Option>
              <Option value="interval">间隔发送</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.send_mode !== currentValues.send_mode}
          >
            {({ getFieldValue }) =>
              getFieldValue('send_mode') === 'personal' && (
                <Form.Item
                  name="user_id"
                  label="用户 ID"
                  rules={[{ required: true, message: '请输入用户 ID' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入用户 ID" />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.send_mode !== currentValues.send_mode}
          >
            {({ getFieldValue }) =>
              getFieldValue('send_mode') === 'scheduled' && (
                <>
                  <Form.Item
                    name="scheduled_time"
                    label="定时发送时间"
                    rules={[{ required: true, message: '请选择定时发送时间' }]}
                  >
                    <DatePicker showTime style={{ width: '100%' }} placeholder="请选择发送时间" />
                  </Form.Item>
                  <Form.Item
                    name="scheduled_type"
                    label="定时类型"
                    initialValue="once"
                  >
                    <Select>
                      <Option value="once">单次</Option>
                      <Option value="daily">每天</Option>
                      <Option value="weekly">每周</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.scheduled_type !== currentValues.scheduled_type}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue('scheduled_type') === 'weekly' && (
                        <Form.Item
                          name="weekday"
                          label="星期几"
                          rules={[{ required: true, message: '请选择星期几' }]}
                        >
                          <Select placeholder="请选择星期几">
                            {WEEKDAY_OPTIONS.map(opt => (
                              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => {
                      const type = prevValues.scheduled_type !== currentValues.scheduled_type;
                      return type || prevValues.scheduled_type === 'daily' || prevValues.scheduled_type === 'weekly';
                    }}
                  >
                    {({ getFieldValue }) =>
                      (getFieldValue('scheduled_type') === 'daily' || getFieldValue('scheduled_type') === 'weekly') && (
                        <Form.Item
                          name="end_time"
                          label="结束日期"
                          rules={[{ required: true, message: '请选择结束日期' }]}
                        >
                          <DatePicker style={{ width: '100%' }} placeholder="请选择结束日期" />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                </>
              )
            }
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.send_mode !== currentValues.send_mode}
          >
            {({ getFieldValue }) =>
              getFieldValue('send_mode') === 'interval' && (
                <>
                  <Form.Item
                    name="interval_value"
                    label="间隔数值"
                    rules={[{ required: true, message: '请输入间隔数值' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入间隔数值" />
                  </Form.Item>
                  <Form.Item
                    name="interval_unit"
                    label="间隔单位"
                    initialValue="minutes"
                  >
                    <Select>
                      {INTERVAL_UNITS.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="end_time"
                    label="结束时间"
                    rules={[{ required: true, message: '请选择结束时间' }]}
                  >
                    <DatePicker showTime style={{ width: '100%' }} placeholder="请选择结束时间" />
                  </Form.Item>
                </>
              )
            }
          </Form.Item>

          <Divider orientation="left">AOZ 类型</Divider>

          <Form.Item
            name="aozCoin"
            label="AOZ Coin"
            rules={[{ type: 'number', min: 0, message: '请输入有效的数量' }]}
          >
            <InputNumber 
              min={0} 
              precision={0} 
              style={{ width: '100%' }} 
              placeholder="请输入 AOZ Coin 数量" 
            />
          </Form.Item>

          <Form.Item
            name="freeAozCoin"
            label="免费 AOZ Coin"
            rules={[{ type: 'number', min: 0, message: '请输入有效的数量' }]}
          >
            <InputNumber 
              min={0} 
              precision={0} 
              style={{ width: '100%' }} 
              placeholder="请输入免费 AOZ Coin 数量" 
            />
          </Form.Item>

          <Form.Item
            name="gmAozCoin"
            label="GM AOZ Coin"
            rules={[{ type: 'number', min: 0, message: '请输入有效的数量' }]}
          >
            <InputNumber 
              min={0} 
              precision={0} 
              style={{ width: '100%' }} 
              placeholder="请输入 GM AOZ Coin 数量" 
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              发送 AOZ
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MailManagement;
